// 高性能智能缓存管理器
export class CacheManager {
  constructor(options = {}) {
    this.maxSize = options.maxSize || 100;
    this.maxAge = options.maxAge || 30000; // 30秒
    this.caches = new Map();
    this.accessTimes = new Map();
    this.cleanupInterval = null;
    this.cleanupBatchSize = options.cleanupBatchSize || 10; // 批量清理大小
    this.memoryThreshold = options.memoryThreshold || 0.8; // 内存阈值

    // 性能监控
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      cleanups: 0,
    };

    // 启动定期清理
    this.startCleanup();
  }

  // 获取缓存 - 优化版本
  get(cacheName, key) {
    const cache = this.caches.get(cacheName);
    if (!cache) {
      this.stats.misses++;
      return null;
    }

    const item = cache.get(key);
    if (!item) {
      this.stats.misses++;
      return null;
    }

    const now = Date.now();
    // 检查是否过期
    if (now - item.timestamp > this.maxAge) {
      this._removeItem(cache, cacheName, key, item);
      this.stats.misses++;
      return null;
    }

    // 更新访问时间 - 使用更高效的方式
    this.accessTimes.set(`${cacheName}:${key}`, now);
    this.stats.hits++;
    return item.value;
  }

  // 检查缓存是否存在 - 优化版本
  has(cacheName, key) {
    const cache = this.caches.get(cacheName);
    if (!cache) return false;

    const item = cache.get(key);
    if (!item) return false;

    // 检查是否过期
    if (Date.now() - item.timestamp > this.maxAge) {
      this._removeItem(cache, cacheName, key, item);
      return false;
    }

    return true;
  }

  // 设置缓存 - 优化版本
  set(cacheName, key, value) {
    if (!this.caches.has(cacheName)) {
      this.caches.set(cacheName, new Map());
    }

    const cache = this.caches.get(cacheName);
    const now = Date.now();

    // 如果缓存已满，使用更高效的清理策略
    if (cache.size >= this.maxSize) {
      this._evictItems(cacheName, cache);
    }

    cache.set(key, {
      value,
      timestamp: now,
    });

    this.accessTimes.set(`${cacheName}:${key}`, now);
  }

  // 删除特定缓存项
  remove(cacheName, key) {
    const cache = this.caches.get(cacheName);
    if (!cache) return false;

    const item = cache.get(key);
    if (!item) return false;

    this._removeItem(cache, cacheName, key, item);
    return true;
  }

  // 私有方法：移除缓存项
  _removeItem(cache, cacheName, key, item) {
    // 如果缓存项有dispose方法，调用它
    if (item.value && typeof item.value.dispose === 'function') {
      try {
        item.value.dispose();
      } catch (error) {
        console.warn('Cache dispose error:', error);
      }
    }

    cache.delete(key);
    this.accessTimes.delete(`${cacheName}:${key}`);
  }

  // 批量清理最旧的缓存项 - 性能优化版本
  _evictItems(cacheName, cache) {
    const entries = Array.from(cache.entries());
    const accessTimeMap = new Map();

    // 预先构建访问时间映射
    for (const [key, item] of entries) {
      const accessKey = `${cacheName}:${key}`;
      accessTimeMap.set(key, this.accessTimes.get(accessKey) || item.timestamp);
    }

    // 按访问时间排序，一次性清理多个项目
    entries.sort((a, b) => accessTimeMap.get(a[0]) - accessTimeMap.get(b[0]));

    const evictCount = Math.min(this.cleanupBatchSize, Math.floor(cache.size * 0.2));
    for (let i = 0; i < evictCount; i++) {
      const [key, item] = entries[i];
      this._removeItem(cache, cacheName, key, item);
      this.stats.evictions++;
    }
  }

  // 清理过期缓存 - 批量优化版本
  cleanup() {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [cacheName, cache] of this.caches.entries()) {
      const expiredKeys = [];

      // 批量收集过期项
      for (const [key, item] of cache.entries()) {
        if (now - item.timestamp > this.maxAge) {
          expiredKeys.push({ key, item });
        }
      }

      // 批量清理
      for (const { key, item } of expiredKeys) {
        this._removeItem(cache, cacheName, key, item);
        cleanedCount++;
      }
    }

    this.stats.cleanups++;
    return cleanedCount;
  }

  // 清理特定缓存 - 优化版本
  clear(cacheName) {
    const cache = this.caches.get(cacheName);
    if (!cache) return;

    // 批量处理dispose
    const items = Array.from(cache.entries());
    for (const [key, item] of items) {
      if (item.value && typeof item.value.dispose === 'function') {
        try {
          item.value.dispose();
        } catch (error) {
          console.warn('Cache dispose error:', error);
        }
      }
      this.accessTimes.delete(`${cacheName}:${key}`);
    }

    cache.clear();
  }

  // 清理所有缓存
  clearAll() {
    for (const cacheName of this.caches.keys()) {
      this.clear(cacheName);
    }
    this.caches.clear();
    this.accessTimes.clear();

    // 重置统计
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      cleanups: 0,
    };
  }

  // 智能清理 - 基于内存使用和命中率
  smartCleanup() {
    // 计算命中率
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;

    // 如果命中率低，更积极地清理
    if (hitRate < 0.5) {
      const cleanedCount = this.cleanup();

      // 清理低效的缓存类型
      for (const [cacheName, cache] of this.caches.entries()) {
        if (cache.size > this.maxSize * this.memoryThreshold) {
          this._evictItems(cacheName, cache);
        }
      }

      return cleanedCount;
    }

    return this.cleanup();
  }

  // 启动定期清理 - 自适应间隔
  startCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    let cleanupInterval = this.maxAge / 2;

    this.cleanupInterval = setInterval(() => {
      const startTime = performance.now();
      const cleanedCount = this.smartCleanup();
      const duration = performance.now() - startTime;

      // 自适应调整清理间隔
      if (duration > 10) {
        // 如果清理耗时超过10ms
        cleanupInterval = Math.min(cleanupInterval * 1.2, this.maxAge);
      } else if (cleanedCount === 0) {
        // 如果没有清理任何项目
        cleanupInterval = Math.min(cleanupInterval * 1.1, this.maxAge);
      } else {
        cleanupInterval = Math.max(cleanupInterval * 0.9, this.maxAge / 4);
      }

      // 重新设置间隔
      if (Math.abs(cleanupInterval - this.maxAge / 2) > 1000) {
        this.startCleanup();
      }
    }, cleanupInterval);
  }

  // 停止定期清理
  stopCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  // 获取缓存统计信息 - 增强版本
  getStats() {
    const cacheStats = {};
    let totalItems = 0;

    for (const [cacheName, cache] of this.caches.entries()) {
      const size = cache.size;
      totalItems += size;

      cacheStats[cacheName] = {
        size,
        maxSize: this.maxSize,
        usage: size / this.maxSize,
      };
    }

    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;

    return {
      caches: cacheStats,
      total: {
        items: totalItems,
        cacheTypes: this.caches.size,
      },
      performance: {
        hitRate,
        hits: this.stats.hits,
        misses: this.stats.misses,
        evictions: this.stats.evictions,
        cleanups: this.stats.cleanups,
      },
    };
  }

  // 内存优化
  optimize() {
    // 强制垃圾回收建议
    if (typeof global !== 'undefined' && global.gc) {
      global.gc();
    }

    // 清理无效的访问时间记录
    const validKeys = new Set();
    for (const [cacheName, cache] of this.caches.entries()) {
      for (const key of cache.keys()) {
        validKeys.add(`${cacheName}:${key}`);
      }
    }

    for (const accessKey of this.accessTimes.keys()) {
      if (!validKeys.has(accessKey)) {
        this.accessTimes.delete(accessKey);
      }
    }
  }

  // 销毁
  dispose() {
    this.stopCleanup();
    this.clearAll();
    this.caches = null;
    this.accessTimes = null;
  }
}
